import React, { useCallback, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useRecipeCreation } from '../../../context/RecipeCreationContext';
import CommonSelect from '../../common/select/CommonSelect';
import { errorToast, successToast } from '../../../utils/helper';

const UNIT_OPTIONS = [
  { label: 'g', value: 'g' },
  { label: 'mg', value: 'mg' },
  { label: 'kcal', value: 'kcal' }
];

const NutritionTab = ({ isView }) => {
  const { recipeData, updateRecipeData } = useRecipeCreation();

  // Current nutrition entry being edited/added
  const [currentNutrition, setCurrentNutrition] = useState({
    id: null,
    ingredient: '',
    amount: '',
    unit: 'g'
  });

  // Handle field changes
  const handleFieldChange = useCallback((field, value) => {
    setCurrentNutrition((prev) => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Save nutrition entry (add new or update existing)
  const handleSaveNutrition = useCallback(() => {
    if (!currentNutrition.ingredient.trim()) {
      errorToast('Nutrition name is required');
      return;
    }

    if (!currentNutrition.amount || parseFloat(currentNutrition.amount) <= 0) {
      errorToast('Amount must be greater than 0');
      return;
    }

    if (!currentNutrition.unit) {
      errorToast('Unit is required');
      return;
    }

    const existingNutrition = recipeData.nutritional_value || [];

    // Add new nutrition entry
    const newNutrition = {
      id: Date.now(),
      ingredient: currentNutrition.ingredient,
      amount: parseFloat(currentNutrition.amount),
      unit: currentNutrition.unit
    };
    const updatedNutrition = [...existingNutrition, newNutrition];
    updateRecipeData({ nutritional_value: updatedNutrition });
    successToast('Nutrition added successfully!');

    // Reset form
    setCurrentNutrition({ id: null, ingredient: '', amount: '', unit: 'g' });
  }, [currentNutrition, recipeData.nutritional_value, updateRecipeData]);

  // Delete nutrition entry
  const handleDeleteNutrition = useCallback(
    (nutritionId) => {
      const existingNutrition = recipeData.nutritional_value || [];
      const updatedNutrition = existingNutrition.filter((item) => item.id !== nutritionId);
      updateRecipeData({ nutritional_value: updatedNutrition });
      successToast('Nutrition removed successfully!');
    },
    [recipeData.nutritional_value, updateRecipeData]
  );

  return (
    <div className="space-y-6">
      {/* Single Nutrition Entry Form */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Nutritional Value
        </h3>

        <div className="grid gap-4 md:grid-cols-12 items-end mb-6">
          {/* Nutrition Name */}
          <div className="md:col-span-5">
            <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
              Nutrition
            </label>
            <input
              type="text"
              placeholder="Nutrition Name"
              value={currentNutrition.ingredient}
              onChange={(e) => handleFieldChange('ingredient', e.target.value)}
              disabled={isView}
              className="w-full rounded-lg px-4 py-2 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
            />
          </div>

          {/* Amount */}
          <div className="md:col-span-3">
            <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
              Amount
            </label>
            <input
              type="number"
              placeholder="00"
              value={currentNutrition.amount}
              onChange={(e) => handleFieldChange('amount', e.target.value)}
              min="0"
              step="0.1"
              disabled={isView}
              className="w-full rounded-lg px-4 py-2 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
            />
          </div>

          {/* Unit */}
          <div className="md:col-span-3">
            <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
              Unit
            </label>
            <CommonSelect
              disabled={isView}
              name="nutrition_unit"
              onChange={(e) => handleFieldChange('unit', e?.value || 'g')}
              value={UNIT_OPTIONS.find((option) => option.value === currentNutrition.unit)}
              options={UNIT_OPTIONS}
              placeholder="Unit"
            />
          </div>
        </div>

        {!isView && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSaveNutrition}
              disabled={!currentNutrition.ingredient.trim() || !currentNutrition.amount}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-shoorah-darkBgTabColor hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <span className="text-lg">+</span>
              Add
            </button>
          </div>
        )}

        {/* List of Saved Nutrition Entries - Inline Editable */}
        {recipeData.nutritional_value && recipeData.nutritional_value.length > 0 && (
          <div className="mt-6 space-y-3">
            {recipeData.nutritional_value.map((nutrition) => (
              <div key={nutrition.id} className="grid gap-4 md:grid-cols-12 items-center">
                {/* Nutrition Name - Editable */}
                <div className="md:col-span-5">
                  <input
                    type="text"
                    value={nutrition.ingredient}
                    onChange={(e) => {
                      const existingNutrition = recipeData.nutritional_value || [];
                      const updatedNutrition = existingNutrition.map((item) =>
                        item.id === nutrition.id ? { ...item, ingredient: e.target.value } : item
                      );
                      updateRecipeData({ nutritional_value: updatedNutrition });
                    }}
                    disabled={isView}
                    className="w-full rounded-lg px-4 py-2 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
                  />
                </div>

                {/* Amount - Editable */}
                <div className="md:col-span-3">
                  <input
                    type="number"
                    value={nutrition.amount}
                    onChange={(e) => {
                      const existingNutrition = recipeData.nutritional_value || [];
                      const updatedNutrition = existingNutrition.map((item) =>
                        item.id === nutrition.id
                          ? { ...item, amount: parseFloat(e.target.value) || 0 }
                          : item
                      );
                      updateRecipeData({ nutritional_value: updatedNutrition });
                    }}
                    min="0"
                    step="0.1"
                    disabled={isView}
                    className="w-full rounded-lg px-4 py-2 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
                  />
                </div>

                {/* Unit - Editable */}
                <div className="md:col-span-3">
                  <CommonSelect
                    disabled={isView}
                    name={`nutrition_unit_${nutrition.id}`}
                    onChange={(e) => {
                      const existingNutrition = recipeData.nutritional_value || [];
                      const updatedNutrition = existingNutrition.map((item) =>
                        item.id === nutrition.id ? { ...item, unit: e?.value || 'g' } : item
                      );
                      updateRecipeData({ nutritional_value: updatedNutrition });
                    }}
                    value={UNIT_OPTIONS.find((option) => option.value === nutrition.unit)}
                    options={UNIT_OPTIONS}
                    placeholder="Unit"
                  />
                </div>

                {/* Delete Button */}
                {!isView && (
                  <div className="md:col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleDeleteNutrition(nutrition.id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete nutrition"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Empty State */}
      {(!recipeData.nutritional_value || recipeData.nutritional_value.length === 0) && (
        <div className="bg-gray-50 dark:bg-shoorah-darkBgColor rounded-lg p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No nutritional values added yet. Add your first nutrition entry above.
          </p>
        </div>
      )}
    </div>
  );
};

export default NutritionTab;
